import React from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, EyeIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { CHALLENGE_IMAGE, challengeCopy, problemCards, proofCopy, type Bi } from '../../data/animalsChallenge';
const EASE = [0.23, 1, 0.32, 1] as const;
const CARD_ICONS = {
  friction: FileTextIcon,
  visibility: EyeIcon,
  impact: BarChart3Icon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 26%)',
  maskImage: 'linear-gradient(to right, transparent 0%, #000 26%)'
};
// Desktop placement of each problem card over the photo (relative to the section wrapper)
const DESKTOP_POS = {
  friction: 'top-[150px] left-[42%] w-[20%]',
  visibility: 'top-[375px] left-[42%] w-[20.5%]',
  impact: 'top-[135px] right-[1.5%] w-[20%]'
} as const;
type Pick = (v: Bi) => string;
function ProblemCard({
  card,
  pick
}: {
  card: (typeof problemCards)[number];
  pick: Pick;
}) {
  const Icon = CARD_ICONS[card.key];
  return <div className="rounded-[24px] border border-[#ECECF4] bg-white/95 shadow-[0_24px_50px_-32px_rgba(40,36,120,0.5)] p-5 flex items-start gap-4" data-id="element-106" data-mp="2ypltd1kxsl0m">
      <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-107" data-mp="33prsf2tjd9sq">
        <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-108" data-mp="2txlyvonmykbf" />
      </span>
      <div className="min-w-0 pt-1" data-id="element-109" data-mp="2tvhn6kjhwh0l">
        <p className="font-sans text-[17px] font-semibold text-[#121A3A]" data-id="element-110" data-mp="2fxtaw13hui75">{pick(card.title)}</p>
        <ul className="mt-1.5 space-y-0.5" data-id="element-111" data-mp="2kxfq2g2vu8il">
          {card.lines.map(l => <li key={l.fr} className="font-sans text-[14.5px] text-[#5B6478] leading-snug" data-id="element-112" data-mp="26xo5txlrvvw5">{pick(l)}</li>)}
        </ul>
      </div>
    </div>;
}
function GrowthGlyph() {
  return <svg viewBox="0 0 180 170" className="w-[150px] h-[140px] shrink-0" fill="none" aria-hidden="true" data-id="element-113" data-mp="561jwagqmr6j">
      <rect x="4" y="130" width="32" height="36" rx="3" fill="#DCDAFB" data-id="element-114" data-mp="97d3nzcxki6t" />
      <rect x="48" y="104" width="32" height="62" rx="3" fill="#DCDAFB" data-id="element-115" data-mp="95ipr502tqc8" />
      <rect x="92" y="80" width="32" height="86" rx="3" fill="#DCDAFB" data-id="element-116" data-mp="1i64tn0vpic6l" />
      <rect x="136" y="46" width="32" height="120" rx="3" fill="#DCDAFB" data-id="element-117" data-mp="1d579wjv1ta2z" />
      <path d="M4 88 C 60 92, 110 70, 160 14" stroke="#4F46E5" strokeWidth="5" strokeLinecap="round" data-id="element-118" data-mp="193lqsgrx7zkc" />
      <path d="M140 12 L166 6 L160 32" stroke="#4F46E5" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" data-id="element-119" data-mp="14524ugdbunrz" />
    </svg>;
}
function ProofCard({
  pick
}: {
  pick: Pick;
}) {
  return <div className="rounded-[26px] border border-[#E6E5F6] bg-[#F7F7FD] shadow-[0_24px_50px_-40px_rgba(40,36,120,0.5)] p-6 sm:p-8 flex items-center gap-6" data-id="element-120" data-mp="1wtpusp90u1bv">
      <div className="min-w-0 flex-1" data-id="element-121" data-mp="1s16ehkiy8s3d">
        <p className="font-sans text-[13px] font-bold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-122" data-mp="1n3o9g581yx0n">{pick(proofCopy.eyebrow)}</p>
        <p className="font-sans text-[3.6rem] sm:text-[4.6rem] font-extrabold text-[#4F46E5] leading-none tracking-tight mt-3" data-id="element-123" data-mp="2kjacz5xcouk3">{pick(proofCopy.value)}</p>
        <p className="font-sans text-[17px] sm:text-[19px] font-semibold text-[#121A3A] leading-snug mt-3 max-w-[440px]" data-id="element-124" data-mp="2fqhaypt9o0bp">{pick(proofCopy.caption)}</p>
        <p className="font-sans text-[13.5px] text-[#6B7385] leading-snug mt-3" data-id="element-125" data-mp="2s3metbitv3r6">{pick(proofCopy.period)}</p>
        <p className="font-sans text-[13.5px] text-[#6B7385] leading-snug" data-id="element-126" data-mp="2x0uzbirg7jvl">{pick(proofCopy.source)}</p>
      </div>
      <div className="hidden sm:block" data-id="element-127" data-mp="314sx9q51eon2">
        <GrowthGlyph data-id="element-128" data-mp="362cf100y1jjr" />
      </div>
    </div>;
}
export function AnimalsChallenge() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick: Pick = v => isFR ? v.fr : v.en;
  const c = challengeCopy;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const heading = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-129" data-mp="3ostysf3st4sz">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.3rem] leading-[1.02] tracking-tight mt-5" data-id="element-130" data-mp="3trdpstx916s7">
        <span className="block text-[#121A3A]" data-id="element-131" data-mp="3g44y7rsmgco5">{pick(c.line1)}</span>
        <span className="block text-[#4F46E5]" data-id="element-132" data-mp="3c3uoq55mf44j">{pick(c.line2)}</span>
        <span className="block text-[#4F46E5]" data-id="element-133" data-mp="3c1ib246wbfxa">{pick(c.line3)}</span>
      </h2>
      <p className="font-sans text-[18px] md:text-[19px] text-[#5B6478] leading-relaxed mt-6 max-w-[580px]" data-id="element-134" data-mp="3puhsibq8nk7h">{pick(c.body)}</p>
    </>;
  return <div className="relative w-full" data-id="element-135" data-mp="2x62bxkrgktu1">
      {/* Desktop — photo on the right, cards layered over it */}
      <div className="hidden xl:block relative max-w-[1760px] mx-auto min-h-[860px]" data-id="element-136" data-mp="2t58pkvtnzn39">
        <div className="absolute inset-y-0 right-0 w-[66%]" aria-hidden="true" data-id="element-137" data-mp="374i1vker1x1y">
          <img src={CHALLENGE_IMAGE} alt="" className="w-full h-full object-cover object-[50%_40%]" style={PHOTO_MASK} data-id="element-138" data-mp="3725e2qmb8sgz" />
        </div>
        <svg className="absolute bottom-0 left-0 w-full h-[160px] pointer-events-none" viewBox="0 0 1440 160" preserveAspectRatio="none" aria-hidden="true" data-id="element-139" data-mp="mdrdj7cohwtn">
          <path d="M0 160 L0 60 C 500 170, 1000 150, 1440 30 L1440 160 Z" fill="#FFFFFF" data-id="element-140" data-mp="hhaevj7us02b" />
        </svg>
        <span className="sr-only" data-id="element-141" data-mp="1zf5q9vd9qljr">{pick(c.imageAlt)}</span>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 w-[38%] pl-6 2xl:pl-10 pt-20" data-id="element-142" data-mp="24cp3qt256uan">
          {heading}
        </motion.div>

        {problemCards.map((card, i) => <motion.div key={card.key} {...reveal} transition={{
        duration: 0.3,
        delay: 0.1 + i * 0.05,
        ease: EASE
      }} className={`absolute z-10 ${DESKTOP_POS[card.key]}`} data-id="element-143" data-mp="1vbr6ctm7ll11" data-mpi={i}>
            <ProblemCard card={card} pick={pick} data-id="element-144" data-mp="2n488b4bteges" />
          </motion.div>)}

        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: EASE
      }} className="absolute z-10 left-6 2xl:left-8 bottom-8 w-[46%] max-w-[800px]" data-id="element-145" data-mp="298wcsupu4lwt">
          <ProofCard pick={pick} data-id="element-146" data-mp="31xl4y48wxgwf" />
        </motion.div>
      </div>

      {/* Tablet / mobile — stacked */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-147" data-mp="36s7j2f6dpnk7">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-148" data-mp="3gl1vd3lth3mr">
          {heading}
        </motion.div>
        <img src={CHALLENGE_IMAGE} alt={pick(c.imageAlt)} className="mt-8 w-full aspect-[4/3] md:aspect-[16/9] rounded-[24px] object-cover object-[60%_40%]" data-id="element-149" data-mp="3bo1owrl21yv4" />
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-150" data-mp="31oug8hp9msec">
          {problemCards.map(card => <ProblemCard key={card.key} card={card} pick={pick} data-id="element-151" data-mp="2xpliz2v0oce1" />)}
        </div>
        <div className="mt-5" data-id="element-152" data-mp="2suph7azzaanq">
          <ProofCard pick={pick} data-id="element-153" data-mp="2nxfva5bssh4n" />
        </div>
      </div>
    </div>;
}